import { useState, useEffect } from "react";
import { format, addDays, parse, setHours, setMinutes } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { Calendar, Clock, Globe, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// Common US timezones
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
];

// Available time slots (9 AM - 6 PM)
const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
];

const formatTimeSlot = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Detect user's timezone
const detectTimezone = (): string => {
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const match = TIMEZONES.find(tz => tz.value === userTz);
  return match ? match.value : "America/Chicago";
};

export interface ScheduledCall {
  date: Date;
  time: string;
  timezone: string;
  utcDateTime: string;
}

interface ConsultationSchedulerProps {
  onScheduleChange: (schedule: ScheduledCall | null) => void;
  selectedSchedule: ScheduledCall | null;
}

export function ConsultationScheduler({ 
  onScheduleChange, 
  selectedSchedule 
}: ConsultationSchedulerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    selectedSchedule?.date
  );
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    selectedSchedule?.time
  );
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    selectedSchedule?.timezone || detectTimezone()
  );
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Minimum date is tomorrow
  const minDate = addDays(new Date(), 1);

  // Disable weekends
  const disableWeekends = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6 || date < minDate;
  };

  const getTimezoneLabel = (tzValue: string): string => {
    const tz = TIMEZONES.find(t => t.value === tzValue);
    return tz?.label ?? "Central Time";
  };

  // Update parent when selection changes - uses proper timezone conversion
  useEffect(() => {
    if (selectedDate && selectedTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      
      // Create a date object with the selected date and time
      let scheduledDate = new Date(selectedDate);
      scheduledDate = setHours(scheduledDate, hours);
      scheduledDate = setMinutes(scheduledDate, minutes);
      scheduledDate.setSeconds(0);
      scheduledDate.setMilliseconds(0);
      
      // Use fromZonedTime to properly convert from the selected timezone to UTC
      // This handles DST correctly by using the IANA timezone identifier
      const utcDate = fromZonedTime(scheduledDate, selectedTimezone);

      onScheduleChange({
        date: selectedDate,
        time: selectedTime,
        timezone: selectedTimezone,
        utcDateTime: utcDate.toISOString(),
      });
    } else {
      onScheduleChange(null);
    }
  }, [selectedDate, selectedTime, selectedTimezone]);

  const isComplete = selectedDate && selectedTime;

  return (
    <div className="space-y-4 p-4 bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg border border-primary/20">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-5 w-5 text-primary" />
        <h4 className="font-semibold text-sm">Schedule Your Consultation</h4>
        {isComplete && (
          <Check className="h-4 w-4 text-green-500 ml-auto" />
        )}
      </div>

      {/* Timezone Selector */}
      <div className="space-y-1.5">
        <label className="text-xs text-muted-foreground flex items-center gap-1">
          <Globe className="h-3 w-3" />
          Your Timezone
        </label>
        <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONES.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date Picker */}
      <div className="space-y-1.5">
        <label className="text-xs text-muted-foreground">Date</label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal h-9 text-sm",
                !selectedDate && "text-muted-foreground"
              )}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {selectedDate ? format(selectedDate, "EEE, MMM d, yyyy") : "Pick a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                setSelectedDate(date);
                setCalendarOpen(false);
              }}
              disabled={disableWeekends}
              initialFocus
              fromDate={minDate}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Time Slots */}
      <div className="space-y-1.5">
        <label className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Time (Business Hours)
        </label>
        <div className="grid grid-cols-3 gap-1.5">
          {TIME_SLOTS.map((time) => (
            <Button
              key={time}
              variant={selectedTime === time ? "default" : "outline"}
              size="sm"
              className={cn(
                "h-8 text-xs",
                selectedTime === time && "ring-2 ring-primary ring-offset-1"
              )}
              onClick={() => setSelectedTime(time)}
            >
              {formatTimeSlot(time)}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary */}
      {isComplete && (
        <div className="mt-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-xs text-green-700 dark:text-green-300 font-medium">
            ✓ Call scheduled for {format(selectedDate!, "EEEE, MMMM d")} at {formatTimeSlot(selectedTime!)} {getTimezoneLabel(selectedTimezone).split(' ')[0]}
          </p>
        </div>
      )}
    </div>
  );
}
